//-------------------------------------------------------------------------------------------------- Imports
import { Card, Col, Row } from "antd";
import { StyleSheet } from "../../../StyleSheet";
import { getDataUsage, getRefillsHistory } from "../../../methods/Methods";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../stores/store";
import { clearAlert, setAlert, showAlert } from "../../../stores/slices/alertSlice";
import CircleOfDataUsage from "./CircleOfDataUsage";
import Loading from "../../../components/Loading";
import TableDetails from "./TableDetails";
import { ResponsePaymentDetails } from "../../../types/common";

//-------------------------------------------------------------------------------------------------- Interfaces
interface DataUsageProps {
    name?: string;
    phone?: string;
    clientId: string;
};

//-------------------------------------------------------------------------------------------------- Component
const DataUsage = (props: DataUsageProps) => {
    const { name, phone, clientId } = props;
    const dispatch = useDispatch<AppDispatch>();
    // Data
    const [saldo, setSaldo] = useState<string>("0 MB");
    const [ofertaOriginal, setOfertaOriginal] = useState<string>("0 MB");
    const [saldoPromo, setSaldoPromo] = useState<string>("0 MB");
    const [ofertaPromo, setOfertaPromo] = useState<string>("0 MB");
    const [serviceType, setServiceType] = useState<string>("");
    const [vigencyNav, setVigencyNav] = useState<string>("");
    const [vigencyPromo, setVigencyPromo] = useState<string>("");
    const [refillHistory, setRefillHistory] = useState<ResponsePaymentDetails[]>([]);
    // Control
    const [showUsage, setShowUsage] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [existPromo, setExistPromo] = useState<boolean>(false);


    const handleFinalizeAlert = useCallback(() => {
        dispatch(showAlert(false));
        dispatch(clearAlert);
    }, [dispatch]);

    const getDataUsageFromAPI = useCallback(async () => {
        if (phone) {
            let error = false;
            let message = "";

            setIsLoading(true);

            await getDataUsage(phone, clientId)
                .then((response) => {
                    if (response.data && response.data.data[0]) {
                        if (response.data.data[0].estatus !== "Error") {
                            if (response.data.data[0].existPromo) {
                                setSaldoPromo(response.data.data[0].consumos.saldoPromo);
                                setOfertaPromo(response.data.data[0].consumos.ofertaPromo);
                                setVigencyPromo(response.data.data[0].consumos.vigenciaPromo);
                            }
                            setExistPromo(response.data.data[0].existPromo);
                            setServiceType(response.data.data[0].consumos.tipo);
                            setSaldo(response.data.data[0].consumos.saldo);
                            setOfertaOriginal(response.data.data[0].consumos.ofertaOriginal);
                            setVigencyNav(response.data.data[0].consumos.vigencia);
                        }
                        else {
                            error = true;
                            message = response.data.data[0].mensaje;
                        }
                    }
                })
                .catch(() => {
                    error = true;
                    message = "No fue posible obtener la información en este momento.\nPor favor intente de nuevo más tarde.";
                });

            if (error) {
                dispatch(setAlert({
                    show: true,
                    type: "ERROR",
                    title: "¡Estimado suscriptor!",
                    message: message,
                    onFinalize: handleFinalizeAlert
                }));
            }
            setIsLoading(false);
        }
    }, [dispatch, handleFinalizeAlert, clientId, phone]);

    const getHistoryRefills = useCallback(() => {
        if (phone) {
            getRefillsHistory(phone, clientId)
                .then((response) => {
                    if (response.data) {
                        setRefillHistory(response.data);
                    }
                })
                .catch(() => { });
        }
    }, [clientId, phone]);

    useEffect(() => {
        getDataUsageFromAPI();
        getHistoryRefills();
    }, [getDataUsageFromAPI, getHistoryRefills]);

    return (
        <Card style={styles.cardStyles} bodyStyle={{ padding: "30px 0px 30px 0px" }}>
            {(isLoading) ? (
                <Row style={styles.usageContainer}>
                    <Loading />
                </Row>
            ) : (
                <>
                    <Row style={styles.bannerStyle}>
                        <Col span={24} style={styles.bannerInfoContainer}>
                            <h2 style={styles.textStyle}>{name}</h2>
                            <p style={styles.textStyle}>{phone}</p>
                            <p style={styles.textStyle}><b>{serviceType.toUpperCase()}</b></p>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24} style={styles.usageContainerText}>
                            <p
                                style={{
                                    ...styles.optionStyleText, ...(showUsage) && { color: "#0055c2" }
                                }}
                                onClick={() => setShowUsage(true)}
                            >
                                Disponible
                            </p>
                            {(refillHistory && refillHistory.length > 0) && (
                                <p
                                    style={{
                                        ...styles.optionStyleText, ...(!showUsage) && { color: "#0055c2" }
                                    }}
                                    onClick={() => setShowUsage(false)}
                                >
                                    Detalles
                                </p>
                            )}
                        </Col>
                    </Row>
                    {(showUsage) ? (
                        <Row style={styles.usageContainer}>
                            <Col sm={24} lg={12} xl={10} style={styles.circleContainer}>
                                <CircleOfDataUsage total={ofertaOriginal} available={saldo} title="Navegación" vigency={vigencyNav} />
                            </Col>
                            {existPromo ? (
                                <Col sm={24} lg={12} xl={10}>
                                    <CircleOfDataUsage total={ofertaPromo} available={saldoPromo} title="Promo" vigency={vigencyPromo} />
                                </Col>
                            ) : null}
                        </Row>
                    ) : (
                        <Row style={styles.detailsContainer}>
                            <Col span={24} style={styles.detailsContainer}>
                                <h3>Tus Recargas</h3>
                            </Col>
                            <Col span={10} style={styles.detailsContainer}>
                                <TableDetails details={refillHistory} />
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </Card>
    );
};

//-------------------------------------------------------------------------------------------------- Styles
const styles = StyleSheet.create({
    cardStyles: {
        marginBottom: 15,
        boxShadow: "0px 0px 8px #00000029",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 20,
    },
    bannerStyle: {
        padding: 20,
        backgroundColor: "#0055c2",
    },
    bannerInfoContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
    },
    textStyle: {
        color: "#ffffff",
        margin: 4,
    },
    usageContainerText: {
        display: "flex",
        margin: "20px 0px 20px 0px",
        justifyContent: "center",
    },
    optionStyleText: {
        cursor: "pointer",
        color: "#707070",
        margin: "0px 20px 0px 20px",
    },
    usageContainer: {
        display: "flex",
        marginTop: 50,
        justifyContent: "center",
    },
    circleContainer: {
        marginBottom: 50,
    },
    detailsContainer: {
        display: "flex",
        justifyContent: "center",
    }
});

//-------------------------------------------------------------------------------------------------- Exports
export default DataUsage;
